import React from 'react';
import { ChevronDownIcon, SettingsIcon, RotateCcwIcon } from 'lucide-react';
import { ANTENNA_DIAMETERS, CHANNEL_BANDWIDTHS, MODULATIONS, type Modulation } from '../utils/linkBudget';
export interface DeviceParams {
  txPowerNE: string;
  txPowerFE: string;
  feederNE: string;
  feederFE: string;
  diameterNE: string;
  diameterFE: string;
  gainNE: string;
  gainFE: string;
  gainNEAuto: boolean;
  gainFEAuto: boolean;
  modulation: Modulation;
  bandwidth: string;
  rxThreshold: string;
  rxThresholdAuto: boolean;
  extraLoss: string;
}
export const DEFAULT_DEVICE_PARAMS: DeviceParams = {
  txPowerNE: '25',
  txPowerFE: '25',
  feederNE: '2.0',
  feederFE: '2.0',
  diameterNE: '0.6',
  diameterFE: '0.6',
  gainNE: '',
  gainFE: '',
  gainNEAuto: true,
  gainFEAuto: true,
  modulation: '128QAM',
  bandwidth: '28',
  rxThreshold: '',
  rxThresholdAuto: true,
  extraLoss: '0'
};
interface DeviceParametersProps {
  open: boolean;
  onToggle: () => void;
  params: DeviceParams;
  onChange: <K extends keyof DeviceParams>(key: K, value: DeviceParams[K]) => void;
  /** Nilai auto yang dihitung dari frekuensi & diameter. */
  autoGainNE: number;
  autoGainFE: number;
  autoThreshold: number;
}
export function DeviceParameters({
  open,
  onToggle,
  params,
  onChange,
  autoGainNE,
  autoGainFE,
  autoThreshold
}: DeviceParametersProps) {
  return <section className="mt-4 rounded-xl border border-slate-700/70 bg-slate-900/40 overflow-hidden" data-id="element-44" data-mp="17zjum1kp1jff">
      <button type="button" onClick={onToggle} aria-expanded={open} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-800/40 transition-colors" data-id="element-45" data-mp="1i02kbcyxvu59">
        <div className="p-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-300" data-id="element-46" data-mp="1rpm20fax1hyj">
          <SettingsIcon size={14} data-id="element-47" data-mp="1vrqtp8ht04ij" />
        </div>
        <div className="flex flex-col leading-tight min-w-0" data-id="element-48" data-mp="1vt1x377c7a94">
          <span className="text-xs font-semibold text-slate-200 uppercase tracking-[0.12em]" data-id="element-49" data-mp="20q0r7pr03h7b">
            Parameter Perangkat
          </span>
          <span className="text-[11px] text-slate-500 truncate" data-id="element-50" data-mp="okdua9b8nss">
            Isi spesifikasi radio dan antena untuk menghitung link budget
            lengkap
          </span>
        </div>
        <ChevronDownIcon size={16} className={`ml-auto text-slate-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} data-id="element-51" data-mp="afosf8v2q6n3" />
      </button>

      {open && <div className="px-4 pb-4 space-y-3 border-t border-slate-800/70 pt-4" data-id="element-52" data-mp="keecn8ag036i">
          {/* Row 1 — daya pancar & feeder */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-53" data-mp="tcq051g0ikuq">
            <NumberField label="Daya Pancar NE (dBm)" value={params.txPowerNE} onChange={v => onChange('txPowerNE', v)} placeholder="25" data-id="element-54" data-mp="tavpcfygg94d" />
            <NumberField label="Daya Pancar FE (dBm)" value={params.txPowerFE} onChange={v => onChange('txPowerFE', v)} placeholder="25" data-id="element-55" data-mp="2v662uwpdjq62" />
            <NumberField label="Loss Feeder NE (dB)" value={params.feederNE} onChange={v => onChange('feederNE', v)} placeholder="2.0" data-id="element-56" data-mp="1amapjflh8j71" />
            <NumberField label="Loss Feeder FE (dB)" value={params.feederFE} onChange={v => onChange('feederFE', v)} placeholder="2.0" data-id="element-57" data-mp="10whb86ty9pz3" />
          </div>

          {/* Row 2 — antena */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-58" data-mp="rxdsj3yp3cl4">
            <SelectField label="Diameter Antena NE (m)" value={params.diameterNE} onChange={v => onChange('diameterNE', v)} options={ANTENNA_DIAMETERS.map(String)} data-id="element-59" data-mp="dwtbac8fhotq" />
            <SelectField label="Diameter Antena FE (m)" value={params.diameterFE} onChange={v => onChange('diameterFE', v)} options={ANTENNA_DIAMETERS.map(String)} data-id="element-60" data-mp="28au8ksoby3rp" />
            <AutoField label="Gain Antena NE (dBi)" auto={params.gainNEAuto} autoValue={autoGainNE} value={params.gainNE} onOverride={() => {
          onChange('gainNEAuto', false);
          onChange('gainNE', autoGainNE.toFixed(1));
        }} onChange={v => onChange('gainNE', v)} onReset={() => onChange('gainNEAuto', true)} data-id="element-61" data-mp="1ilx6tayhi20b" />
            <AutoField label="Gain Antena FE (dBi)" auto={params.gainFEAuto} autoValue={autoGainFE} value={params.gainFE} onOverride={() => {
          onChange('gainFEAuto', false);
          onChange('gainFE', autoGainFE.toFixed(1));
        }} onChange={v => onChange('gainFE', v)} onReset={() => onChange('gainFEAuto', true)} data-id="element-62" data-mp="ztukpk32ptio" />
          </div>

          {/* Row 3 — modulasi & threshold */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-63" data-mp="2sm9dzhk9omgf">
            <SelectField label="Modulasi" value={params.modulation} onChange={v => onChange('modulation', v as Modulation)} options={[...MODULATIONS]} data-id="element-64" data-mp="2wjxcsvp2yzdl" />
            <SelectField label="Bandwidth Kanal (MHz)" value={params.bandwidth} onChange={v => onChange('bandwidth', v)} options={CHANNEL_BANDWIDTHS.map(String)} data-id="element-65" data-mp="36hlj8iw1tvbl" />
            <AutoField label="RX Threshold (dBm)" auto={params.rxThresholdAuto} autoValue={autoThreshold} value={params.rxThreshold} onOverride={() => {
          onChange('rxThresholdAuto', false);
          onChange('rxThreshold', autoThreshold.toFixed(1));
        }} onChange={v => onChange('rxThreshold', v)} onReset={() => onChange('rxThresholdAuto', true)} data-id="element-66" data-mp="2iudcdwiz2e7g" />
            <NumberField label="Loss Radome (dB)" value={params.extraLoss} onChange={v => onChange('extraLoss', v)} placeholder="0" data-id="element-67" data-mp="1j2hqx3775ikp" />
          </div>
        </div>}
    </section>;
}
function FieldLabel({
  children
}: {
  children: React.ReactNode;
}) {
  return <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.12em]" data-id="element-68" data-mp="145qpkodypwsg">
      {children}
    </span>;
}
function NumberField({
  label,
  value,
  onChange,
  placeholder
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return <label className="flex flex-col gap-1.5" data-id="element-69" data-mp="193acecoq7hw0">
      <FieldLabel data-id="element-70" data-mp="2pibnrt8i1w1i">{label}</FieldLabel>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} inputMode="decimal" className="input-dark w-full text-sm py-2 font-mono" data-id="element-71" data-mp="2ph91jym4ghrx" />
    </label>;
}
function SelectField({
  label,
  value,
  onChange,
  options
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
}) {
  return <label className="flex flex-col gap-1.5" data-id="element-72" data-mp="3thh04k7jc7ry">
      <FieldLabel data-id="element-73" data-mp="29us6fds9j5a">{label}</FieldLabel>
      <select value={value} onChange={e => onChange(e.target.value)} className="filter-select text-sm rounded-lg px-3 py-2 border border-slate-700/70 text-slate-200 outline-none hover:border-slate-600 transition-colors font-mono" data-id="element-74" data-mp="2aw4en6jcbol">
        {options.map(option => <option key={option} value={option} className="bg-slate-900" data-id="element-75" data-mp="g8kqtzg93qvr">
            {option}
          </option>)}
      </select>
    </label>;
}
function AutoField({
  label,
  auto,
  autoValue,
  value,
  onOverride,
  onChange,
  onReset
}: {
  label: string;
  auto: boolean;
  autoValue: number;
  value: string;
  onOverride: () => void;
  onChange: (value: string) => void;
  onReset: () => void;
}) {
  return <label className="flex flex-col gap-1.5" data-id="element-76" data-mp="39tqxuvfdt2wk">
      <div className="flex items-center gap-1.5" data-id="element-77" data-mp="351plzam4le72">
        <FieldLabel data-id="element-78" data-mp="34zuxxnfk55yr">{label}</FieldLabel>
        {auto ? <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700 uppercase tracking-wider" data-id="element-79" data-mp="301l4pide8ffs">
            auto
          </span> : <button type="button" onClick={onReset} title="Kembalikan ke nilai auto" className="flex items-center gap-1 text-[8px] font-bold px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-400/40 uppercase tracking-wider hover:bg-cyan-500/25 transition-colors" data-id="element-80" data-mp="3srjvpjb5emn3">
            <RotateCcwIcon size={8} data-id="element-81" data-mp="3eqzeusosn0w8" />
            manual
          </button>}
      </div>

      {auto ? <button type="button" onClick={onOverride} title="Klik untuk mengisi manual" className="text-left w-full text-sm py-2 px-3 rounded-lg font-mono bg-slate-900/60 border border-slate-800 text-slate-500 hover:border-cyan-400/40 hover:text-slate-300 transition-colors" data-id="element-82" data-mp="27bkxhcvf6q9r">
          {autoValue.toFixed(1)}
        </button> : <input value={value} onChange={e => onChange(e.target.value)} inputMode="decimal" autoFocus className="input-dark w-full text-sm py-2 font-mono" data-id="element-83" data-mp="rtewdeqhe30e" />}
    </label>;
}